import React, { useState } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Linking,
  Text,
} from "react-native";
import { StyleSheet, scale } from "@/utils/appStyleSheet";
import { useRouter } from "expo-router";
import { Colors } from "@/constants/Colors";
import { Button } from "@/components/Button";
import { Ionicons } from "@expo/vector-icons";
import useUserStore from "@/store/user";
import useSubscriptionPayment from "@/hooks/sharedHook/useSubscriptionPayment";
import { IAP_ANNUAL_PLANS, IAP_MONTHLY_PLANS } from "@/constants/Configs";
import Images from "@/assets/images";
import { Image } from "expo-image";
import Icons from "@/assets/icons";
import { FontFamilies } from "@/constants/Typography";

type PlanType = typeof IAP_ANNUAL_PLANS | typeof IAP_MONTHLY_PLANS;

export default function OnboardPaywallScreen() {
  const { getUserProfile, userProfile } = useUserStore();
  const router = useRouter();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(IAP_ANNUAL_PLANS);

  // Ensure user profile is loaded
  React.useEffect(() => {
    if (!userProfile?.id) {
      getUserProfile();
    }
  }, [userProfile?.id, getUserProfile]);

  const { requestSubscription, loading } = useSubscriptionPayment({
    sku: selectedPlan.sku,
    userId: userProfile?.id,
    onSuccess: async () => {
      await getUserProfile();
      router.replace("/signup/onboard-success");
    },
  });

  const handleContinue = () => {
    if (!userProfile?.id) {
      console.error("Cannot proceed: userProfile.id is not available");
      return;
    }
    requestSubscription();
  };

  const handleClose = () => {
    router.replace("/signup/onboard-paywall-offer");
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Close Button */}
        <TouchableOpacity
          style={styles.closeButton}
          onPress={handleClose}
          disabled={loading}
        >
          <Ionicons
            name="close"
            size={scale(32)}
            color={loading ? "#A3A3A3" : "#000"}
          />
        </TouchableOpacity>

        {/* Rocket Image */}
        <View style={styles.imageContainer}>
          <Image source={Images.rocket} style={styles.rocketImage} />
        </View>

        {/* Title and Description */}
        <View style={styles.textContainer}>
          <Text style={styles.title}>Start filing{"\n"}claims today</Text>
          <Text style={styles.description}>
            The average user claims $345 per year.
          </Text>
        </View>

        {/* Money Back Guarantee Badge */}
        <View style={styles.guaranteeContainer}>
          <View style={styles.laurelLeft}>
            <Icons.Leaves
              style={{
                transform: [{ scaleX: -1 }],
                width: scale(28),
                height: scale(60),
              }}
            />
          </View>
          <View style={styles.guaranteeBadge}>
            <View style={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Ionicons
                  key={star}
                  name="star"
                  size={scale(12)}
                  color="#FFB800"
                  style={styles.star}
                />
              ))}
            </View>
            <Text style={styles.guaranteeText}>Money Back Guaranteed</Text>
            <Text style={styles.guaranteeSubtext}>
              Try for a year. Terms Apply
            </Text>
          </View>
          <View style={styles.laurelRight}>
            <Icons.Leaves style={{ width: scale(28), height: scale(60) }} />
          </View>
        </View>

        {/* Plan Selection */}
        <View style={styles.plansContainer}>
          {/* Monthly Plan */}
          <TouchableOpacity
            style={[
              styles.planCard,
              selectedPlan === IAP_MONTHLY_PLANS && styles.planCardSelected,
              loading && styles.planCardDisabled,
            ]}
            onPress={() => setSelectedPlan(IAP_MONTHLY_PLANS)}
            activeOpacity={0.8}
            disabled={loading}
          >
            <View style={styles.planContent}>
              <View style={styles.radioContainer}>
                <View
                  style={[
                    styles.radioOuter,
                    selectedPlan === IAP_MONTHLY_PLANS &&
                      styles.radioOuterSelected,
                  ]}
                >
                  {selectedPlan === IAP_MONTHLY_PLANS && (
                    <View style={styles.radioInner} />
                  )}
                </View>
              </View>
              <View style={styles.planDetails}>
                <View style={styles.planHeader}>
                  <Text style={styles.planTitle}>Monthly Plan</Text>
                  <Text style={styles.planPrice}>$15/month</Text>
                </View>
                <Text style={styles.planDescription}>
                  File without limits. Cancel anytime.
                </Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* Annual Plan */}
          <View>
            <TouchableOpacity
              style={[
                styles.planCard,
                selectedPlan === IAP_ANNUAL_PLANS && styles.planCardSelected,
                loading && styles.planCardDisabled,
              ]}
              onPress={() => setSelectedPlan(IAP_ANNUAL_PLANS)}
              activeOpacity={0.8}
              disabled={loading}
            >
              <View style={styles.planContent}>
                <View style={styles.radioContainer}>
                  <View
                    style={[
                      styles.radioOuter,
                      selectedPlan === IAP_ANNUAL_PLANS &&
                        styles.radioOuterSelected,
                    ]}
                  >
                    {selectedPlan === IAP_ANNUAL_PLANS && (
                      <View style={styles.radioInner} />
                    )}
                  </View>
                </View>
                <View style={styles.planDetails}>
                  <View style={styles.planHeader}>
                    <Text style={styles.planTitle}>Annual Plan</Text>
                    <Text style={styles.planPrice}>$84/year</Text>
                  </View>
                  <Text style={styles.planDescription}>
                    File without limits. Billed annually.
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
            {/* Discount Badge */}
            <View style={styles.discountBadge}>
              <Text style={styles.discountText}>50% OFF</Text>
            </View>
          </View>
        </View>

        {/* Continue Button */}
        <Button
          title={loading ? "Processing..." : "Continue"}
          variant="primary"
          style={styles.continueButton}
          onPress={handleContinue}
          disabled={loading || !userProfile?.id}
        />
        <Text style={styles.termsText}>
          By continuing you agree to{"\n"}our{" "}
          <Text
            style={styles.termsTextLink}
            onPress={() => Linking.openURL("https://usesparrow.com/terms")}
          >
            Terms of Use
          </Text>{" "}
          and{" "}
          <Text
            style={styles.termsTextLink}
            onPress={() =>
              Linking.openURL("https://usesparrow.com/class-action-terms")
            }
          >
            Class Action Terms
          </Text>
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#fff",
  },
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  contentContainer: {
    paddingHorizontal: 32,
    paddingBottom: 40,
  },
  closeButton: {
    alignSelf: "flex-end",
    padding: 8,
    marginTop: 8,
  },
  imageContainer: {
    alignItems: "center",
    marginVertical: 24,
  },
  rocketImage: {
    width: 100,
    height: 100,
    alignSelf: "center",
    resizeMode: "contain",
  },
  textContainer: {
    alignItems: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontFamily: FontFamilies.Inter.SemiBold,
    textAlign: "center",
    lineHeight: 35,
    color: "#000",
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    textAlign: "center",
    color: "#000",
    lineHeight: 23,
  },
  guaranteeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  laurelLeft: {
    marginRight: 8,
  },
  laurelRight: {
    marginLeft: 8,
  },
  guaranteeBadge: {
    alignItems: "center",
  },
  starsContainer: {
    flexDirection: "row",
    marginBottom: 4,
  },
  star: {
    marginHorizontal: 1,
  },
  guaranteeText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 2,
  },
  guaranteeSubtext: {
    fontSize: 13,
    color: "#161D14",
    opacity: 0.5,
    fontFamily: FontFamilies.Inter.Medium,
  },
  plansContainer: {
    gap: 24,
    marginBottom: 24,
  },
  planCard: {
    borderWidth: 1,
    borderColor: "#A3A3A3",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#fff",
  },
  planCardSelected: {
    borderColor: Colors.primary,
  },
  planCardDisabled: {
    opacity: 0.5,
  },
  planContent: {
    flexDirection: "row",
    gap: 4,
  },
  radioContainer: {
    paddingTop: 2,
  },
  radioOuter: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#A3A3A3",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuterSelected: {
    borderColor: Colors.primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.primary,
  },
  planDetails: {
    flex: 1,
  },
  planHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  planTitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#000",
  },
  planPrice: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#000",
  },
  planDescription: {
    fontSize: 11,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#000",
    lineHeight: 15,
  },
  discountBadge: {
    position: "absolute",
    top: -10,
    left: 34,
    backgroundColor: "#329A30",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
  },
  discountText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 10,
    color: "#fff",
  },
  continueButton: {
    marginBottom: 16,
    backgroundColor: Colors.primary,
  },
  termsText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
    lineHeight: 16,
  },
  termsTextLink: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.primary,
  },
});
